import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Search, HeartPulse, Boxes, LayoutDashboard, Ticket, PanelsTopLeft, X, Check, TrendingUp } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const CASES = [
  {
    num: "01", title: "Enterprise AI Experiences", focus: "AI-powered search, answer generation and large-scale consumer & enterprise experiences.", tags: ["AI Search", "Generative AI", "Scale"], Icon: Search, hue: "from-[#4E2178] to-[#7A2EA6]",
    overview: "We designed and engineered AI-powered search and answer-generation experiences serving consumer and enterprise audiences at massive scale — systems where relevance, speed and trust are non-negotiable.",
    highlights: ["Intent-aware search architecture across billions of data points", "Generative answer pipelines with grounded, cited responses", "Evaluation harnesses measuring answer quality and safety continuously", "Feedback and analytics loops that make answers better every week"],
    outcomes: ["Answers in milliseconds, trusted at consumer scale", "Measurably higher resolution without human escalation", "A reusable AI foundation now powering multiple products"],
    stats: ["Consumer scale", "Sub-second answers", "RAG + GenAI"],
  },
  {
    num: "02", title: "Digital Healthcare", focus: "Cloud-native patient engagement platform supporting scheduling, virtual visits, check-in, messaging and connected healthcare workflows.", tags: ["Cloud-Native", "Patient Engagement"], Icon: HeartPulse, hue: "from-[#D12D8C] to-[#E86A33]",
    overview: "A cloud-native patient engagement platform connecting scheduling, virtual visits, digital check-in and secure messaging into one continuous care journey — built for reliability where it matters most.",
    highlights: ["Event-driven, cloud-native architecture with zero-downtime deployments", "Virtual visit infrastructure with real-time notifications", "Digital check-in flows that cut front-desk workload", "Integration-ready layer designed for EHR and clinical systems"],
    outcomes: ["Smoother patient journeys from booking to follow-up", "Significantly reduced administrative burden on staff", "A platform that scales across clinics and specialties"],
    stats: ["Cloud-native", "Real-time visits", "Connected workflows"],
  },
  {
    num: "03", title: "AI + Supply Chain", focus: "AI-assisted assortment planning, intelligent automation, LLM-enabled operational workflows and large-scale product & location data.", tags: ["Agentic AI", "LLM Workflows"], Icon: Boxes, hue: "from-[#2C1650] to-[#D12D8C]",
    overview: "AI-assisted assortment planning and LLM-enabled operational workflows operating over large-scale product and location data — turning weeks of manual analysis into guided, intelligent decisions.",
    highlights: ["Agentic planning assistants that reason over assortment data", "Document and data intelligence across massive product catalogs", "Human-in-the-loop approvals exactly where judgment matters", "Pipelines processing product and location data at enterprise scale"],
    outcomes: ["Planning cycles compressed from weeks to days", "Consistent, explainable decisions across regions", "Teams focused on exceptions instead of spreadsheets"],
    stats: ["Agentic workflows", "Large-scale data", "Human-in-the-loop"],
  },
  {
    num: "04", title: "Enterprise Digital Experience", focus: "AEM, DAM, metadata, SEO automation, commerce integrations and digital experience modernization.", tags: ["AEM", "DAM", "Commerce"], Icon: PanelsTopLeft, hue: "from-[#4E2178] to-[#D12D8C]",
    overview: "Enterprise digital experience modernization across Adobe Experience Manager, DAM, metadata intelligence, SEO automation and commerce — a content ecosystem that operates as one platform.",
    highlights: ["Content supply chain automation from creation to publish", "Metadata intelligence making every asset findable", "SEO automation applied continuously, not quarterly", "Commerce and API integrations unifying the experience stack"],
    outcomes: ["Publishing velocity multiplied across teams", "Consistent discoverability across every property", "Lower content operations cost at higher quality"],
    stats: ["AEM + DAM", "SEO automation", "Commerce integrated"],
  },
  {
    num: "05", title: "Event Technology", focus: "Multi-tenant registration, payments, QR check-in, hotel, food, analytics, operations and event automation.", tags: ["SaaS", "Operations"], Icon: Ticket, hue: "from-[#E86A33] to-[#D12D8C]",
    overview: "A multi-tenant event operations platform covering registration, payments, QR check-in, hotel and food logistics, analytics and on-site automation — one system from invite to closing keynote.",
    highlights: ["Multi-tenant SaaS architecture serving many events in parallel", "Integrated payments and registration flows", "QR-based check-in eliminating on-site queues", "Live operations dashboards for organizers and staff"],
    outcomes: ["Check-in lines replaced by scan-and-go moments", "One operational view across every event function", "Repeatable playbooks that make each event easier"],
    stats: ["Multi-tenant", "QR check-in", "Live analytics"],
  },
  {
    num: "06", title: "Digital Platforms", focus: "Custom SaaS, portals, operational dashboards and workflow platforms.", tags: ["Custom SaaS", "Dashboards"], Icon: LayoutDashboard, hue: "from-[#17121C] to-[#4E2178]",
    overview: "Custom SaaS products, client portals, operational dashboards and workflow platforms — engineered from idea to production, designed around how each business actually operates.",
    highlights: ["Design-to-deployment product engineering under one roof", "Role-based portals for clients, partners and teams", "Operational dashboards turning data into daily decisions", "Automation built into the platform, not bolted on later"],
    outcomes: ["Manual workflows replaced by intelligent systems", "Real-time visibility across operations", "Platforms that grow with the business"],
    stats: ["Custom SaaS", "Role-based portals", "Built-in automation"],
  },
];

export const SelectedWork = ({ onContact }) => {
  const [open, setOpen] = useState(null);

  return (
  <section id="work" data-testid="selected-work-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading
          testId="selected-work"
          overline="Selected Work"
          title={<>Technology built around <span className="jx-gradient-text">real business problems.</span></>}
        />
        <FadeUp delay={0.2}>
          <button
            data-testid="work-cta-button"
            onClick={onContact}
            className="group inline-flex items-center gap-2 rounded-full border border-jx-ink/15 px-6 py-3 text-sm font-medium text-jx-ink hover:border-jx-purple/40 hover:bg-jx-purple/5 transition-[border-color,background-color] duration-300"
          >
            Explore Our Work
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </FadeUp>
      </div>

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CASES.map((c, i) => (
          <FadeUp key={c.num} delay={(i % 3) * 0.1}>
            <motion.article
              data-testid={`case-card-${i}`}
              onClick={() => setOpen(c)}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="group h-full rounded-3xl bg-white border border-jx-ink/8 overflow-hidden flex flex-col cursor-pointer hover:shadow-[0_24px_50px_rgb(0,0,0,0.09)] transition-shadow duration-300"
            >
              <div className={`relative h-44 bg-gradient-to-br ${c.hue} overflow-hidden`}>
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, white 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                >
                  <c.Icon className="w-14 h-14 text-white/90" strokeWidth={1.2} />
                </motion.div>
                <span className="absolute top-4 left-5 font-display font-extrabold text-white/25 text-4xl">{c.num}</span>
                <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl tracking-tight text-jx-ink">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-jx-ink/55 flex-1">{c.focus}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {c.tags.map((t) => (
                    <span key={t} className="rounded-full bg-jx-mist px-3 py-1 text-[11px] font-semibold text-jx-purple">{t}</span>
                  ))}
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-jx-magenta opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Read the story <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </motion.article>
          </FadeUp>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="case-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-6 bg-jx-night/60 backdrop-blur-sm"
            onClick={() => setOpen(null)}
          >
            <motion.article
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white relative"
            >
              <div className={`relative h-40 bg-gradient-to-br ${open.hue} overflow-hidden`}>
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, white 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
                <span className="absolute bottom-5 left-8 font-display font-extrabold text-white/25 text-6xl">{open.num}</span>
                <open.Icon className="absolute bottom-6 right-8 w-12 h-12 text-white/85" strokeWidth={1.2} />
              </div>
              <button
                data-testid="case-modal-close"
                onClick={() => setOpen(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white transition-colors"
                aria-label="Close case study"
              >
                <X className="w-5 h-5 text-jx-ink" />
              </button>
              <div className="p-8 sm:p-12">
                <p className="jx-overline text-jx-purple">Case Study</p>
                <h3 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl tracking-tighter text-jx-ink leading-tight">{open.title}</h3>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {open.stats.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5 rounded-full jx-gradient-bg px-4 py-1.5 text-xs font-semibold text-white">
                      <TrendingUp className="w-3.5 h-3.5" />{s}
                    </span>
                  ))}
                </div>
                <p className="mt-7 text-lg leading-relaxed text-jx-ink/75 font-medium">{open.overview}</p>
                <p className="mt-10 jx-overline text-jx-magenta">What we engineered</p>
                <div className="mt-4 space-y-3">
                  {open.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm sm:text-base text-jx-ink/70" data-testid={`case-highlight-${i}`}>
                      <Check className="w-4 h-4 text-jx-copper shrink-0 mt-1" strokeWidth={2.5} />
                      {h}
                    </div>
                  ))}
                </div>
                <p className="mt-10 jx-overline text-jx-magenta">Business outcomes</p>
                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                  {open.outcomes.map((o, i) => (
                    <div key={i} className="rounded-2xl bg-jx-mist border border-jx-ink/6 p-5 text-sm font-medium text-jx-ink/75 leading-snug" data-testid={`case-outcome-${i}`}>{o}</div>
                  ))}
                </div>
                <button
                  data-testid="case-modal-cta"
                  onClick={() => { setOpen(null); onContact(); }}
                  className="group mt-10 inline-flex items-center gap-3 rounded-full jx-gradient-bg text-white font-medium px-7 py-3.5 transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                >
                  Discuss a similar project
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </section>
  );
};
